/**
 * LogoDotMatrix Web Component
 * High-performance, Canvas 2D ambient dot grid with fluid wave motion
 * for section flanks.
 */
class LogoDotMatrix extends HTMLElement {
  constructor() {
    super();
    this.style.display = 'block';
    this.style.position = 'absolute';
    this.style.top = '0';
    this.style.left = '0';
    this.style.width = '100%';
    this.style.height = '100%';
    this.style.pointerEvents = 'none';
    this.style.zIndex = '1';

    this.canvas = document.createElement('canvas');
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = '1';
    this.appendChild(this.canvas);

    this.ctx = null;
    this.raf = null;
    this.ro = null;
    this.io = null;

    this.width = 0;
    this.height = 0;
    this.dpr = 1;

    this.isVisible = false;
    this.hasEntered = false;
    this.entranceStartTime = null;
    this.entranceDuration = 1400; // ms

    this.dotsLeft = [];
    this.dotsRight = [];
    this.reducedMotion = false;
  }

  connectedCallback() {
    this.ctx = this.canvas.getContext('2d');
    if (!this.ctx) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Resize Observer
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(this);

    // Intersection Observer to pause/play animation
    this.io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.isVisible = true;
          if (!this.hasEntered) {
            this.hasEntered = true;
            this.entranceStartTime = performance.now();
          }
          if (!this.raf) {
            this.raf = requestAnimationFrame((t) => this.render(t));
          }
        } else {
          this.isVisible = false;
          if (this.raf) {
            cancelAnimationFrame(this.raf);
            this.raf = null;
          }
        }
      });
    }, {
      threshold: [0, 0.1, 0.2]
    });

    this.io.observe(this);
    this.resize();
  }

  disconnectedCallback() {
    if (this.ro) this.ro.disconnect();
    if (this.io) this.io.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  parseHex(hex) {
    let c = (hex || '#D10000').replace('#', '');
    if (c.length === 3) c = c.split('').map(x => x + x).join('');
    const num = parseInt(c, 16);
    return {
      r: (num >> 16) & 255,
      g: (num >> 8) & 255,
      b: num & 255
    };
  }

  resize() {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = this.clientWidth;
    this.height = this.clientHeight;

    this.canvas.width = Math.floor(this.width * this.dpr);
    this.canvas.height = Math.floor(this.height * this.dpr);
    if (this.ctx) {
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    }

    this.generateDots();
  }

  generateDots() {
    if (this.width <= 0 || this.height <= 0) return;

    const coveragePct = Math.min(30, Math.max(10, parseFloat(this.getAttribute('coverage')) || 20)) / 100;
    const isMobile = this.width < 768;
    
    // Width of each flank
    const flankWidth = isMobile ? this.width * Math.min(coveragePct, 0.15) : this.width * coveragePct;

    // Grid spacing & sizing
    const targetCols = isMobile ? 3 : 5;
    const colSpacing = flankWidth / (targetCols + 0.8);
    const rowSpacing = isMobile ? 24 : 28;
    const baseRadius = isMobile ? 3.5 : 4.5;

    const numRows = Math.floor((this.height - 20) / rowSpacing);
    const yStart = (this.height - (numRows - 1) * rowSpacing) / 2;

    this.dotsLeft = [];
    this.dotsRight = [];

    // Generate Left Flank
    for (let c = 0; c < targetCols; c++) {
      const xTarget = (c + 0.8) * colSpacing;
      // Stagger delays based on column distance from edge (outermost arrives first)
      const colDelay = c * 75; // ms

      for (let r = 0; r < numRows; r++) {
        const yTarget = yStart + r * rowSpacing;
        const rowDelay = (r % 2 === 0 ? 0 : 35);
        this.dotsLeft.push({
          xTarget,
          yTarget,
          col: c,
          row: r,
          delay: colDelay + rowDelay,
          radius: baseRadius,
          feather: 1 - Math.pow(c / targetCols, 1.4) * 0.75 // subtle fade towards center
        });
      }
    }

    // Generate Right Flank
    for (let c = 0; c < targetCols; c++) {
      const xTarget = this.width - (c + 0.8) * colSpacing;
      const colDelay = c * 75; // ms

      for (let r = 0; r < numRows; r++) {
        const yTarget = yStart + r * rowSpacing;
        const rowDelay = (r % 2 === 0 ? 0 : 35);
        this.dotsRight.push({
          xTarget,
          yTarget,
          col: c,
          row: r,
          delay: colDelay + rowDelay,
          radius: baseRadius,
          feather: 1 - Math.pow(c / targetCols, 1.4) * 0.75
        });
      }
    }
  }

  // Smooth ease-out quintic
  easeOut(t) {
    return 1 - Math.pow(1 - t, 4);
  }

  render(timestamp) {
    if (!this.isVisible) {
      this.raf = null;
      return;
    }

    this.ctx.clearRect(0, 0, this.width, this.height);

    const baseColorHex = this.getAttribute('color') || '#D10000';
    const rgb = this.parseHex(baseColorHex);
    const speed = parseFloat(this.getAttribute('speed')) || 1.6;

    const timeSec = timestamp * 0.001 * speed;
    const elapsed = this.entranceStartTime ? timestamp - this.entranceStartTime : 0;

    const coveragePct = (parseFloat(this.getAttribute('coverage')) || 20) / 100;
    const flankWidth = this.width * coveragePct;

    // Draw Left Flank Dots
    for (let i = 0; i < this.dotsLeft.length; i++) {
      const dot = this.dotsLeft[i];
      let currentX, currentY, alpha, currentRadius;

      if (this.reducedMotion) {
        currentX = dot.xTarget;
        currentY = dot.yTarget;
        alpha = dot.feather;
        currentRadius = dot.radius;
      } else {
        // Entrance progress
        const dotElapsed = Math.max(0, elapsed - dot.delay);
        const progress = Math.min(1, dotElapsed / this.entranceDuration);
        const eased = this.easeOut(progress);

        // Slide in from outside left (starts off-screen)
        const startX = dot.xTarget - flankWidth - 60;
        currentX = startX + (dot.xTarget - startX) * eased;

        // Ambient fluid wave undulation
        const waveY = Math.sin(timeSec + dot.col * 0.45 + dot.row * 0.35) * 3.5;
        const waveX = Math.cos(timeSec * 0.75 + dot.row * 0.4) * 1.5;
        currentY = dot.yTarget + waveY * eased;
        currentX += waveX * eased;

        // Subtle breathing scale
        const pulse = Math.sin(timeSec * 1.2 + dot.col * 0.5 + dot.row * 0.3) * 0.15;
        currentRadius = Math.max(1, dot.radius * (1 + pulse * eased));

        alpha = dot.feather * eased;
      }

      if (alpha > 0.01) {
        this.drawDot(currentX, currentY, currentRadius, rgb, alpha);
      }
    }

    // Draw Right Flank Dots
    for (let i = 0; i < this.dotsRight.length; i++) {
      const dot = this.dotsRight[i];
      let currentX, currentY, alpha, currentRadius;

      if (this.reducedMotion) {
        currentX = dot.xTarget;
        currentY = dot.yTarget;
        alpha = dot.feather;
        currentRadius = dot.radius;
      } else {
        const dotElapsed = Math.max(0, elapsed - dot.delay);
        const progress = Math.min(1, dotElapsed / this.entranceDuration);
        const eased = this.easeOut(progress);

        // Slide in from outside right (starts off-screen)
        const startX = dot.xTarget + flankWidth + 60;
        currentX = startX + (dot.xTarget - startX) * eased;

        // Ambient fluid wave undulation
        const waveY = Math.sin(timeSec + dot.col * 0.45 + dot.row * 0.35) * 3.5;
        const waveX = -Math.cos(timeSec * 0.75 + dot.row * 0.4) * 1.5;
        currentY = dot.yTarget + waveY * eased;
        currentX += waveX * eased;

        const pulse = Math.sin(timeSec * 1.2 + dot.col * 0.5 + dot.row * 0.3) * 0.15;
        currentRadius = Math.max(1, dot.radius * (1 + pulse * eased));

        alpha = dot.feather * eased;
      }

      if (alpha > 0.01) {
        this.drawDot(currentX, currentY, currentRadius, rgb, alpha);
      }
    }

    this.raf = requestAnimationFrame((t) => this.render(t));
  }

  drawDot(x, y, radius, rgb, alpha) {
    const ctx = this.ctx;
    ctx.save();

    // 3D sphere gradient highlight matching the reference image
    const grad = ctx.createRadialGradient(
      x - radius * 0.3,
      y - radius * 0.3,
      radius * 0.1,
      x,
      y,
      radius
    );
    
    // Light highlight at top-left
    const highlightR = Math.min(255, rgb.r + 55);
    const highlightG = Math.min(255, rgb.g + 50);
    const highlightB = Math.min(255, rgb.b + 50);
    
    grad.addColorStop(0, `rgba(${highlightR}, ${highlightG}, ${highlightB}, ${alpha})`);
    grad.addColorStop(0.7, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha * 0.95})`);
    grad.addColorStop(1, `rgba(${Math.max(0, rgb.r - 40)}, ${rgb.g * 0.5}, ${rgb.b * 0.5}, ${alpha * 0.9})`);

    // Subtle soft glow halo
    ctx.shadowColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha * 0.55})`;
    ctx.shadowBlur = radius * 1.2;

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }
}

if (!customElements.get('logo-dot-matrix')) {
  customElements.define('logo-dot-matrix', LogoDotMatrix);
}
